/**
 * La répartition d'un paiement : ce qui va sur les factures, et ce qu'on fait
 * du reste.
 *
 * ## Le défaut que ce module corrige
 *
 * Signalé le 4 septembre 2026 : une mère paie 60,00 €, sa facture est annulée,
 * il lui reste une facture de 35,00 €.
 *
 *   « quand je l'affecte, les 35 euros sont bien affectés mais les 25 euros
 *     qui restent disparaissent. Alors qu'elle devrait avoir 25 euros de
 *     provisions. Si je clique sur reste en provision tout en laissant les 35
 *     euros à affecter à la facture alors il met 25 euros en provision mais il
 *     ne paye pas la facture de 35... »
 *
 * Deux boutons, deux écritures séparées, **et chacune ignorait l'autre** :
 *
 * - « Affecter » écrivait les lignes de facture et rien d'autre. Le reliquat
 *   restait dans la file — mais la facture étant désormais payée, la famille
 *   n'avait plus de facture ouverte, donc plus de suggestion, donc plus de
 *   bouton « Reste en provision ». Le reliquat devenait inatteignable : de
 *   l'écran, il avait disparu.
 * - « Reste en provision » écrivait la provision et rien d'autre : les
 *   montants saisis dans les cases des factures étaient jetés.
 *
 * Les deux gestes n'étaient pas des alternatives : ils devaient composer.
 *
 * ## Le remède
 *
 * **Une répartition, une écriture.** Ce module construit la liste complète des
 * lignes — factures et destination du reliquat — et `allocatePayment` les
 * insère en un seul statement. Il n'y a plus d'état intermédiaire où l'argent
 * est à moitié placé.
 *
 * Et le reliquat a **toujours une destination nommée**, y compris « laissé
 * dans la file ». Un reliquat sans destination explicite est exactement ce qui
 * a fait disparaître 25,00 €.
 */

/** Ce que l'administration décide de faire du reliquat. */
export type DestinationDuReste =
  /* Il reste dans la file, à traiter plus tard. Un choix, pas un oubli. */
  | 'file'
  /* Il va à la provision de la famille : elle paiera une facture future. */
  | 'provision'
  /* Il n'est pas du ressort des centres de vacances (garderie, partenaire). */
  | 'hors_perimetre';

/** Une ligne prête à être écrite, telle que la base l'attend. */
export interface LigneARepartir {
  target: 'invoice' | 'provision' | 'out_of_scope';
  amountCents: number;
  invoiceId?: string;
  provisionUserId?: string;
  note?: string;
}

export interface DemandeDeRepartition {
  /** Ce qui reste à placer sur ce paiement, en centimes. */
  unallocatedCents: number;
  /** Les montants saisis par facture, en centimes. Les zéros sont ignorés. */
  parts: Record<string, number>;
  /** La famille dont la provision recevra le reliquat, si c'est le choix. */
  provisionUserId: string | null;
  destinationDuReste: DestinationDuReste;
  /** Ce qui sera consigné sur la ligne du reliquat. */
  noteDuReste?: string;
}

export interface Repartition {
  lignes: LigneARepartir[];
  /** Le total placé sur des factures. */
  totalFactures: number;
  /** Ce qui n'ira pas sur une facture. */
  reste: number;
  /**
   * La raison pour laquelle cette répartition ne peut pas être écrite, ou
   * `null`. Une phrase française, affichable telle quelle.
   */
  probleme: string | null;
}

export function construireRepartition(d: DemandeDeRepartition): Repartition {
  const lignesFactures = Object.entries(d.parts)
    .map(([invoiceId, amountCents]) => ({ invoiceId, amountCents: amountCents || 0 }))
    .filter((l) => l.amountCents > 0);

  const totalFactures = lignesFactures.reduce((s, l) => s + l.amountCents, 0);
  const reste = d.unallocatedCents - totalFactures;

  const lignes: LigneARepartir[] = lignesFactures.map((l) => ({
    target: 'invoice' as const,
    amountCents: l.amountCents,
    invoiceId: l.invoiceId,
  }));

  /*
    Un dépassement est refusé ici plutôt qu'à l'écriture. La base le refuserait
    aussi — c'est l'invariant 1 — mais avec un message qui parle de centimes et
    d'identifiants.
  */
  if (reste < 0) {
    return {
      lignes: [], totalFactures, reste,
      probleme: `La répartition dépasse le paiement de ${eurosCourts(-reste)}.`,
    };
  }

  /*
    Une négation ou un zéro dans une case : refusé. La base a un CHECK
    `amount_cents > 0`, et une case a -5,00 € est une faute de frappe qu'il
    vaut mieux signaler que traduire en refus de contrainte.
  */
  const negative = Object.values(d.parts).some((v) => (v ?? 0) < 0);
  if (negative) {
    return { lignes: [], totalFactures, reste, probleme: 'Un montant négatif a été saisi.' };
  }

  if (reste > 0) {
    if (d.destinationDuReste === 'provision') {
      if (!d.provisionUserId) {
        return {
          lignes: [], totalFactures, reste,
          probleme: 'Pour verser le reste en provision, il faut désigner la famille.',
        };
      }
      lignes.push({
        target: 'provision',
        amountCents: reste,
        provisionUserId: d.provisionUserId,
        note: d.noteDuReste ?? 'Reliquat versé en provision',
      });
    } else if (d.destinationDuReste === 'hors_perimetre') {
      lignes.push({
        target: 'out_of_scope',
        amountCents: reste,
        note: d.noteDuReste ?? 'Reliquat hors périmètre',
      });
    }
    /* 'file' : aucune ligne. Le reliquat reste à traiter, et c'est assumé. */
  }

  if (lignes.length === 0) {
    return {
      lignes: [], totalFactures, reste,
      probleme: 'Rien à écrire : aucun montant réparti.',
    };
  }

  return { lignes, totalFactures, reste, probleme: null };
}

/**
 * Ce que le bouton va faire, en une phrase.
 *
 * L'ancien écran affichait « Affecter » quoi qu'il arrive, et n'annonçait
 * jamais le sort du reliquat. Le libellé dit désormais tout ce qui sera écrit :
 * c'est la dernière chose que l'administration lit avant de cliquer.
 */
export function phraseDeRepartition(r: Repartition, d: DemandeDeRepartition): string {
  if (r.probleme) return r.probleme;

  const morceaux: string[] = [];
  const nbFactures = r.lignes.filter((l) => l.target === 'invoice').length;
  if (nbFactures > 0) {
    morceaux.push(
      nbFactures === 1
        ? `${eurosCourts(r.totalFactures)} sur la facture`
        : `${eurosCourts(r.totalFactures)} sur ${nbFactures} factures`,
    );
  }

  if (r.reste > 0) {
    if (d.destinationDuReste === 'provision') morceaux.push(`${eurosCourts(r.reste)} en provision`);
    else if (d.destinationDuReste === 'hors_perimetre') morceaux.push(`${eurosCourts(r.reste)} hors périmètre`);
    else morceaux.push(`${eurosCourts(r.reste)} laissés dans la file`);
  }

  return morceaux.join(', ');
}

/**
 * Vrai si le paiement sera entièrement placé par cette répartition.
 *
 * Utile pour l'avertir : un reliquat laissé dans la file est un choix
 * légitime, mais qui doit être vu.
 */
export function placeEntierement(r: Repartition, d: DemandeDeRepartition): boolean {
  return r.probleme === null && (r.reste === 0 || d.destinationDuReste !== 'file');
}

/* Le format court, pour des phrases. `money.ts` sert à l'affichage des tableaux. */
function eurosCourts(centimes: number): string {
  return `${(centimes / 100).toFixed(2).replace('.', ',')} €`;
}
